import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Search, ChevronDown, ChevronRight, FileText, Plus, Loader2,
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, ClipboardList,
  ArrowLeft, Save, Trash2, Eye, Filter, Grid3X3
} from "lucide-react";

const STATUS_CONFIG = {
  "compliant": { icon: CheckCircle2, color: "#4ADE80", label: "Compliant", bg: "rgba(74,222,128,0.12)" },
  "partial": { icon: AlertTriangle, color: "#FBBF24", label: "Partial", bg: "rgba(251,191,36,0.12)" },
  "non-compliant": { icon: XCircle, color: "#F87171", label: "Non-Compliant", bg: "rgba(248,113,113,0.12)" },
  "not-applicable": { icon: MinusCircle, color: "#64748B", label: "N/A", bg: "rgba(100,116,139,0.12)" },
  "not-assessed": { icon: ClipboardList, color: "#94A3B8", label: "Not Assessed", bg: "rgba(148,163,184,0.08)" },
};

const INDUSTRY_COLORS = {
  "medical-devices": { accent: "#00C8E0", bg: "rgba(0,200,224,0.08)", border: "rgba(0,200,224,0.3)" },
  "biotechnology": { accent: "#A78BFA", bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.3)" },
  "aerospace": { accent: "#FB923C", bg: "rgba(251,146,60,0.08)", border: "rgba(251,146,60,0.3)" },
  "electronics-telecom": { accent: "#4ADE80", bg: "rgba(74,222,128,0.08)", border: "rgba(74,222,128,0.3)" },
  "labs-metrology": { accent: "#F472B6", bg: "rgba(244,114,182,0.08)", border: "rgba(244,114,182,0.3)" },
};

// ─── Matrix Browser View ───
const MatrixBrowser = ({ industries, onGenerateTemplate }) => {
  const { t } = useTranslation('audit');
  const [activeIndustry, setActiveIndustry] = useState(null);
  const [industryData, setIndustryData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [globalResults, setGlobalResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState("");

  const loadIndustry = useCallback(async (slug) => {
    setLoading(true);
    setSearchResults(null);
    setGlobalResults(null);
    setSearchQuery("");
    try {
      const res = await axios.get(`${API}/audit-matrices/${slug}`, { withCredentials: true });
      setIndustryData(res.data);
      setActiveIndustry(slug);
      const expanded = {};
      res.data.sections.forEach(s => { expanded[s.slug] = true; });
      setExpandedSections(expanded);
      setSelectedSections([]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (industries.length > 0 && !activeIndustry) {
      loadIndustry(industries[0].slug);
    }
  }, [industries, activeIndustry, loadIndustry]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setGlobalResults(null);
    setSearchResults(null);
    try {
      const res = await axios.get(`${API}/audit-matrices-search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setGlobalResults(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const toggleSection = (slug) => {
    setExpandedSections(prev => ({ ...prev, [slug]: !prev[slug] }));
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSectionSelect = (slug) => {
    setSelectedSections(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const handleGenerateTemplate = () => {
    if (!templateName.trim()) return;
    onGenerateTemplate({
      name: templateName,
      industry_slug: activeIndustry,
      section_slugs: selectedSections,
      description: `Audit template for ${industryData?.industry || activeIndustry}`
    });
    setShowTemplateForm(false);
    setTemplateName("");
    setSelectedSections([]);
  };

  const colors = INDUSTRY_COLORS[activeIndustry] || INDUSTRY_COLORS["medical-devices"];
  const displaySections = searchResults ? searchResults.sections : (industryData?.sections || []);

  return (
    <div>
      {/* Industry Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="industry-tabs">
        {industries.map(ind => {
          const c = INDUSTRY_COLORS[ind.slug] || INDUSTRY_COLORS["medical-devices"];
          const isActive = activeIndustry === ind.slug;
          return (
            <button
              key={ind.slug}
              data-testid={`industry-tab-${ind.slug}`}
              onClick={() => loadIndustry(ind.slug)}
              className="px-4 py-2.5 rounded-[3px] text-sm font-medium transition-all"
              style={{
                background: isActive ? c.bg : "transparent",
                border: `1px solid ${isActive ? c.border : "#1E3A5F"}`,
                color: isActive ? c.accent : "#94A3B8",
              }}
            >
              {ind.industry}
              <span className="ml-2 text-xs opacity-60">({ind.total_items})</span>
            </button>
          );
        })}
      </div>

      {/* Search + Actions */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <input
              data-testid="matrix-search-input"
              type="text"
              placeholder={t('matrices.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              className="w-full pl-10 pr-4 py-2.5 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] text-white text-sm placeholder-[#64748B] focus:border-[#00C8E0] outline-none"
            />
          </div>
          <button
            data-testid="matrix-search-btn"
            onClick={handleSearch}
            className="px-4 py-2.5 bg-[#1A6BCC] text-white text-sm rounded-[3px] hover:bg-[#2080E0] transition-colors"
          >
            Search
          </button>
          {(searchResults || globalResults) && (
            <button
              onClick={() => { setSearchResults(null); setGlobalResults(null); setSearchQuery(""); }}
              className="px-3 py-2.5 border border-[#1E3A5F] text-[#94A3B8] text-sm rounded-[3px] hover:text-white hover:border-[#64748B] transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <button
          data-testid="generate-template-btn"
          onClick={() => setShowTemplateForm(!showTemplateForm)}
          className="px-4 py-2.5 rounded-[3px] text-sm font-medium flex items-center gap-2 transition-all"
          style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.accent }}
        >
          <Plus className="w-4 h-4" /> Generate Audit Template
        </button>
      </div>

      {/* Template Form */}
      {showTemplateForm && (
        <div data-testid="template-form" className="mb-6 p-5 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
          <h3 className="text-white font-medium mb-3">Create Audit Template</h3>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              data-testid="template-name-input"
              type="text"
              placeholder="Template name (e.g., Q1 2026 FDA Audit)"
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              className="flex-1 px-4 py-2.5 bg-[#132340] border border-[#1E3A5F] rounded-[3px] text-white text-sm placeholder-[#64748B] focus:border-[#00C8E0] outline-none"
            />
            <button
              data-testid="create-template-btn"
              onClick={handleGenerateTemplate}
              disabled={!templateName.trim()}
              className="px-5 py-2.5 bg-[#1A6BCC] text-white text-sm rounded-[3px] hover:bg-[#2080E0] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Create Template
            </button>
          </div>
          <p className="text-xs text-[#64748B] mb-3">Select specific sections below to include, or leave empty to include all sections:</p>
          <div className="flex flex-wrap gap-2">
            {(industryData?.sections || []).map(s => (
              <button
                key={s.slug}
                data-testid={`section-select-${s.slug}`}
                onClick={() => toggleSectionSelect(s.slug)}
                className="px-3 py-1.5 text-xs rounded-[3px] transition-all"
                style={{
                  background: selectedSections.includes(s.slug) ? colors.bg : "transparent",
                  border: `1px solid ${selectedSections.includes(s.slug) ? colors.border : "#1E3A5F"}`,
                  color: selectedSections.includes(s.slug) ? colors.accent : "#94A3B8",
                }}
              >
                {s.title} ({s.items.length})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#00C8E0]" />
        </div>
      )}

      {/* Search Results Banner */}
      {globalResults && (
        <div className="mb-4 px-4 py-2.5 rounded-[3px] text-sm bg-[rgba(0,200,224,0.08)] border border-[rgba(0,200,224,0.3)] text-[#00C8E0]">
          Found {globalResults.total_matches} results across {globalResults.results.length} {globalResults.results.length === 1 ? 'industry' : 'industries'} for "{globalResults.query}"
        </div>
      )}

      {/* Global Search Results */}
      {!loading && globalResults && globalResults.results.map(indResult => {
        const indColors = INDUSTRY_COLORS[indResult.slug] || INDUSTRY_COLORS["medical-devices"];
        return (
          <div key={indResult.slug} className="mb-6">
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: indColors.accent }}>{indResult.industry}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-[3px]" style={{ background: indColors.bg, color: indColors.accent }}>{indResult.match_count} matches</span>
            </div>
            {indResult.sections.map(section => (
              <div key={section.slug} className="mb-3" data-testid={`search-section-${indResult.slug}-${section.slug}`}>
                <button
                  onClick={() => toggleSection(`${indResult.slug}_${section.slug}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-[#132340] border border-[#1E3A5F] rounded-[3px] hover:border-[#2A4A6F] transition-colors text-left"
                >
                  {expandedSections[`${indResult.slug}_${section.slug}`]
                    ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: indColors.accent }} />
                    : <ChevronRight className="w-4 h-4 flex-shrink-0 text-[#64748B]" />
                  }
                  <span className="text-white font-medium text-sm flex-1">{section.title}</span>
                  <span className="text-xs px-2 py-0.5 rounded-[3px]" style={{ background: indColors.bg, color: indColors.accent }}>
                    {section.items.length} items
                  </span>
                </button>
                {expandedSections[`${indResult.slug}_${section.slug}`] && (
                  <div className="mt-1 space-y-1 ml-4 border-l pl-4" style={{ borderColor: indColors.border }}>
                    {section.items.map(item => (
                      <div key={item.item_id} className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] overflow-hidden">
                        <button onClick={() => toggleItem(item.item_id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#132340] transition-colors text-left">
                          {expandedItems[item.item_id]
                            ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: indColors.accent }} />
                            : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-[#64748B]" />
                          }
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-sm font-medium">{item.title}</span>
                            <span className="text-[#64748B] text-xs ml-2">— {item.description}</span>
                          </div>
                        </button>
                        {expandedItems[item.item_id] && (
                          <div className="px-4 pb-3 pt-0 ml-7">
                            <div className="text-[#94A3B8] text-xs leading-relaxed space-y-1">
                              {item.details.map((d, i) => <p key={i}>{d}</p>)}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {/* Regular Sections (no global search active) */}
      {!loading && !globalResults && displaySections.map(section => (
        <div key={section.slug} className="mb-4" data-testid={`section-${section.slug}`}>
          <button
            data-testid={`section-toggle-${section.slug}`}
            onClick={() => toggleSection(section.slug)}
            className="w-full flex items-center gap-3 px-4 py-3 bg-[#132340] border border-[#1E3A5F] rounded-[3px] hover:border-[#2A4A6F] transition-colors text-left"
          >
            {expandedSections[section.slug]
              ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: colors.accent }} />
              : <ChevronRight className="w-4 h-4 flex-shrink-0 text-[#64748B]" />
            }
            <span className="text-white font-medium text-sm flex-1">{section.title}</span>
            <span className="text-xs px-2 py-0.5 rounded-[3px]" style={{ background: colors.bg, color: colors.accent }}>
              {section.items.length} items
            </span>
          </button>

          {expandedSections[section.slug] && (
            <div className="mt-1 space-y-1 ml-4 border-l border-[#1E3A5F] pl-4">
              {section.items.map(item => (
                <div key={item.item_id} className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] overflow-hidden" data-testid={`item-${item.item_id}`}>
                  <button
                    onClick={() => toggleItem(item.item_id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#132340] transition-colors text-left"
                  >
                    {expandedItems[item.item_id]
                      ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.accent }} />
                      : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-[#64748B]" />
                    }
                    <div className="flex-1 min-w-0">
                      <span className="text-white text-sm font-medium">{item.title}</span>
                      <span className="text-[#64748B] text-xs ml-2">— {item.description}</span>
                    </div>
                  </button>
                  {expandedItems[item.item_id] && (
                    <div className="px-4 pb-3 pt-0 ml-7">
                      <div className="text-[#94A3B8] text-xs leading-relaxed space-y-1">
                        {item.details.map((d, i) => (
                          <p key={i}>{d}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Template List View ───
const TemplateList = ({ templates, onSelect, onDelete, loading }) => {
  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[#00C8E0]" />
    </div>
  );

  if (templates.length === 0) return (
    <div className="text-center py-16">
      <ClipboardList className="w-12 h-12 mx-auto mb-4 text-[#1E3A5F]" />
      <p className="text-[#64748B] text-sm">No audit templates yet</p>
      <p className="text-[#475569] text-xs mt-1">Generate a template from the Matrix Browser tab</p>
    </div>
  );

  return (
    <div className="grid gap-3">
      {templates.map(item => {
        const colors = INDUSTRY_COLORS[item.industry_slug] || INDUSTRY_COLORS["medical-devices"];
        return (
          <div
            key={item.template_id}
            data-testid={`template-card-${item.template_id}`}
            className="flex items-center gap-4 p-4 bg-[#132340] border border-[#1E3A5F] rounded-[3px] hover:border-[#2A4A6F] transition-colors"
          >
            <div className="flex-1 min-w-0">
              <h3 className="text-white text-sm font-medium truncate">{item.name}</h3>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-xs px-2 py-0.5 rounded-[3px]" style={{ background: colors.bg, color: colors.accent, border: `1px solid ${colors.border}` }}>
                  {item.industry}
                </span>
                <span className="text-[#64748B] text-xs">{new Date(item.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <button
              data-testid={`template-open-${item.template_id}`}
              onClick={() => onSelect(item.template_id)}
              className="px-3 py-2 text-xs text-[#00C8E0] border border-[#00C8E0]/30 rounded-[3px] hover:bg-[#00C8E0]/10 transition-colors flex items-center gap-1.5"
            >
              <Eye className="w-3.5 h-3.5" /> Open
            </button>
            <button
              data-testid={`template-delete-${item.template_id}`}
              onClick={() => onDelete(item.template_id)}
              className="px-3 py-2 text-xs text-[#F87171] border border-[#F87171]/30 rounded-[3px] hover:bg-[#F87171]/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ─── Template Detail (Compliance Tracker) ───
const TemplateDetail = ({ templateId, onBack }) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSection, setFilterSection] = useState("all");
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    loadTemplate();
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-templates/${templateId}`, { withCredentials: true });
      setTemplate(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateItem = async (itemId, field, value) => {
    setSaving(prev => ({ ...prev, [itemId]: true }));
    try {
      await axios.put(`${API}/audit-templates/${templateId}/items/${itemId}`, { [field]: value }, { withCredentials: true });
      setTemplate(prev => ({
        ...prev,
        items: prev.items.map(it => it.item_id === itemId ? { ...it, [field]: value } : it)
      }));
    } catch (e) { console.error(e); }
    setSaving(prev => ({ ...prev, [itemId]: false }));
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[#00C8E0]" />
    </div>
  );

  if (!template) return (
    <div className="text-center py-12 text-[#64748B]">Template not found</div>
  );

  const colors = INDUSTRY_COLORS[template.industry_slug] || INDUSTRY_COLORS["medical-devices"];
  const sections = [...new Set(template.items.map(i => i.section_title))];

  const filteredItems = template.items.filter(item => {
    if (filterStatus !== "all" && item.status !== filterStatus) return false;
    if (filterSection !== "all" && item.section_title !== filterSection) return false;
    return true;
  });

  const groupedItems = {};
  filteredItems.forEach(item => {
    if (!groupedItems[item.section_title]) groupedItems[item.section_title] = [];
    groupedItems[item.section_title].push(item);
  });

  // Stats
  const stats = {};
  template.items.forEach(item => {
    stats[item.status] = (stats[item.status] || 0) + 1;
  });
  const total = template.items.length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          data-testid="template-back-btn"
          onClick={onBack}
          className="p-2 rounded-[3px] border border-[#1E3A5F] text-[#94A3B8] hover:text-white hover:border-[#64748B] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h2 className="text-white font-semibold text-lg">{template.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs px-2 py-0.5 rounded-[3px]" style={{ background: colors.bg, color: colors.accent, border: `1px solid ${colors.border}` }}>
              {template.industry}
            </span>
            <span className="text-[#64748B] text-xs">{total} clauses</span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-5 gap-2 mb-6" data-testid="template-stats">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = stats[key] || 0;
          const Icon = cfg.icon;
          return (
            <div key={key} className="p-3 rounded-[3px] border text-center" style={{ background: cfg.bg, borderColor: `${cfg.color}33` }}>
              <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: cfg.color }} />
              <div className="text-lg font-semibold" style={{ color: cfg.color }}>{count}</div>
              <div className="text-[10px] text-[#94A3B8] uppercase tracking-wide">{cfg.label}</div>
            </div>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="h-2 bg-[#132340] rounded-full overflow-hidden flex">
          {["compliant", "partial", "non-compliant", "not-applicable", "not-assessed"].map(key => {
            const count = stats[key] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            return pct > 0 ? (
              <div key={key} style={{ width: `${pct}%`, backgroundColor: STATUS_CONFIG[key].color }} className="transition-all" />
            ) : null;
          })}
        </div>
        <div className="text-xs text-[#64748B] mt-1 text-right">
          {Math.round(((stats["compliant"] || 0) / total) * 100)}% compliant
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-[#64748B]" />
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] text-white text-xs px-3 py-2 focus:border-[#00C8E0] outline-none"
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <select
          data-testid="filter-section"
          value={filterSection}
          onChange={e => setFilterSection(e.target.value)}
          className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] text-white text-xs px-3 py-2 focus:border-[#00C8E0] outline-none"
        >
          <option value="all">All Sections</option>
          {sections.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Grouped Items */}
      {Object.entries(groupedItems).map(([sectionTitle, items]) => (
        <div key={sectionTitle} className="mb-5">
          <h3 className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 px-1">{sectionTitle}</h3>
          <div className="space-y-1">
            {items.map(item => {
              const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG["not-assessed"];
              const StatusIcon = statusCfg.icon;
              const isExpanded = expandedItems[item.item_id];
              return (
                <div key={item.item_id} data-testid={`template-item-${item.item_id}`} className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <StatusIcon className="w-4 h-4 flex-shrink-0" style={{ color: statusCfg.color }} />
                    <button onClick={() => toggleItem(item.item_id)} className="flex-1 text-left min-w-0">
                      <span className="text-white text-sm font-medium">{item.title}</span>
                      <span className="text-[#64748B] text-xs ml-2 hidden sm:inline">— {item.description}</span>
                    </button>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {saving[item.item_id] && <Loader2 className="w-3 h-3 animate-spin text-[#64748B]" />}
                      <select
                        data-testid={`status-select-${item.item_id}`}
                        value={item.status}
                        onChange={e => updateItem(item.item_id, "status", e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-[3px] border outline-none cursor-pointer"
                        style={{ background: statusCfg.bg, borderColor: `${statusCfg.color}44`, color: statusCfg.color }}
                      >
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                          <option key={k} value={k} style={{ background: "#132340", color: "#fff" }}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 ml-7 space-y-3">
                      <div className="text-[#94A3B8] text-xs leading-relaxed space-y-1">
                        {item.details.map((d, i) => <p key={i}>{d}</p>)}
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Auditor Notes</label>
                        <textarea
                          data-testid={`notes-input-${item.item_id}`}
                          value={item.notes || ""}
                          onChange={e => {
                            setTemplate(prev => ({
                              ...prev,
                              items: prev.items.map(it => it.item_id === item.item_id ? { ...it, notes: e.target.value } : it)
                            }));
                          }}
                          onBlur={e => updateItem(item.item_id, "notes", e.target.value)}
                          rows={2}
                          placeholder={t('matrices.observations')}
                          className="w-full mt-1 px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-[3px] text-white text-xs placeholder-[#475569] focus:border-[#00C8E0] outline-none resize-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Evidence Reference</label>
                        <input
                          data-testid={`evidence-input-${item.item_id}`}
                          type="text"
                          value={item.evidence || ""}
                          onChange={e => {
                            setTemplate(prev => ({
                              ...prev,
                              items: prev.items.map(it => it.item_id === item.item_id ? { ...it, evidence: e.target.value } : it)
                            }));
                          }}
                          onBlur={e => updateItem(item.item_id, "evidence", e.target.value)}
                          placeholder={t('matrices.docReference')}
                          className="w-full mt-1 px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-[3px] text-white text-xs placeholder-[#475569] focus:border-[#00C8E0] outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ───
const AuditMatricesPage = () => {
  const { t } = useTranslation(['audit', 'common']);
  const [view, setView] = useState("browser"); // browser, templates, detail
  const [industries, setIndustries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    loadIndustries();
    loadTemplates();
  }, []);

  const loadIndustries = async () => {
    try {
      const res = await axios.get(`${API}/audit-matrices`, { withCredentials: true });
      setIndustries(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${API}/audit-templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch (e) { console.error(e); }
    setTemplatesLoading(false);
  };

  const handleGenerateTemplate = async (formData) => {
    try {
      const res = await axios.post(`${API}/audit-templates`, formData, { withCredentials: true });
      setSelectedTemplateId(res.data.template_id);
      setView("detail");
      loadTemplates();
    } catch (e) { console.error(e); }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/audit-templates/${templateId}`, { withCredentials: true });
      setTemplates(prev => prev.filter(t => t.template_id !== templateId));
    } catch (e) { console.error(e); }
  };

  if (view === "detail" && selectedTemplateId) {
    return (
      <QMSLayout title={t('common:modules.audit')}>
        <div className="max-w-5xl mx-auto">
          <TemplateDetail
            templateId={selectedTemplateId}
            onBack={() => { setView("templates"); setSelectedTemplateId(null); }}
          />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('common:modules.audit')}>
      <div className="max-w-5xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-1" style={{ fontFamily: "'Syne', sans-serif" }}>
            Intelligence Knowledge Hub
          </h1>
          <p className="text-[#64748B] text-sm">
            Dynamic audit matrices across Medical Devices, BioTechnology, Aerospace, Electronics & Telecommunications, and Labs, Metrology & Test Centers
          </p>
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 mb-6 p-1 bg-[#0F1E35] rounded-[3px] w-fit" data-testid="view-tabs">
          <button
            data-testid="tab-browser"
            onClick={() => setView("browser")}
            className={`px-4 py-2 text-sm rounded-[3px] transition-colors ${view === "browser" ? "bg-[#132340] text-white" : "text-[#64748B] hover:text-[#94A3B8]"}`}
          >
            <Grid3X3 className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Matrix Browser
          </button>
          <button
            data-testid="tab-templates"
            onClick={() => { setView("templates"); loadTemplates(); }}
            className={`px-4 py-2 text-sm rounded-[3px] transition-colors ${view === "templates" ? "bg-[#132340] text-white" : "text-[#64748B] hover:text-[#94A3B8]"}`}
          >
            <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            My Templates
            {templates.length > 0 && (
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-[#1A6BCC]/20 text-[#00C8E0]">{templates.length}</span>
            )}
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#00C8E0]" />
          </div>
        ) : view === "browser" ? (
          <MatrixBrowser industries={industries} onGenerateTemplate={handleGenerateTemplate} />
        ) : (
          <TemplateList
            templates={templates}
            onSelect={(id) => { setSelectedTemplateId(id); setView("detail"); }}
            onDelete={handleDeleteTemplate}
            loading={templatesLoading}
          />
        )}
      </div>
    </QMSLayout>
  );
};

export default AuditMatricesPage;
